import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid, Title } from '@mantine/core'
import {
  CheckboxFieldController,
  NumberInputController,
  PhoneInputController,
  TextAreaController,
  TextInputController,
} from '@/components/FormElements'
import { useAppState } from '@/features/app/hooks'
import { useAuth } from '@/features/auth'
import { BOOKING_TYPE } from '@/features/bookings/consts/booking'

interface IProps {
  frame: boolean
}

type FormValues = {
  has_special_needs: boolean
  entertainment_kit: boolean
  extra_activities: any
  special_needs: string
  //other_info: string
  care_expectations: string
  phone: string
  contact_email: string
  first_name: string
  address: string
}

export const InfoFormSection = ({ frame }: IProps) => {
  const { t } = useTranslation()
  const { isLoggedIn } = useAuth()
  const { booking } = useSelector((state: any) => state.booking.checkout)
  const {
    appState: { mobileView },
  } = useAppState()

  const { control, watch, setValue } = useFormContext<FormValues>()

  const watchHasSpecialNeeds = watch('has_special_needs')
  const watchBalloons = watch('extra_activities.balloon_modeling')
  const watchPainting = watch('extra_activities.facial_painting')

  useEffect(() => {
    if (!watchBalloons && !watchPainting) setValue('extra_activities.hours', 0)
  }, [watchBalloons, watchPainting])

  return (
    <>
      <CheckboxFieldController
        control={control}
        name={'has_special_needs'}
        id={'has_special_needs'}
        label={t(
          booking.type === BOOKING_TYPE.ANIMATION
            ? 'children_has_special_needs'
            : 'my_kid_has_special_needs'
        )}
        size={mobileView ? 'md' : 'lg'}
        mb={'md'}
      />

      {watchHasSpecialNeeds && (
        <TextAreaController
          control={control}
          name={'special_needs'}
          id={'special_needs'}
          label={t('special_needs')}
          size={mobileView ? 'md' : 'lg'}
          placeholder={t('special_needs-field.placeholder')}
          description={
            booking?.type === BOOKING_TYPE.ANIMATION ? '' : t('special_needs-field.description')
          }
          mb={'md'}
          styles={
            mobileView
              ? {
                  label: {
                    fontWeight: 'bold',
                    fontSize: '16px !important',
                  },
                }
              : {}
          }
        />
      )}

      {booking.type == BOOKING_TYPE.ANIMATION && (
        <>
          <CheckboxFieldController
            control={control}
            name={'entertainment_kit'}
            id={'entertainment_kit'}
            label={t('add_entertainment_kit')}
            size={mobileView ? 'md' : 'lg'}
            mb={'md'}
          />

          <Title order={5}>
            <div className="flex flex-col">{t('extra_activities')}</div>
          </Title>

          <CheckboxFieldController
            control={control}
            name={'extra_activities.facial_painting'}
            id={'extra_activities.facial_painting'}
            label={t('facial_painting')}
            size={mobileView ? 'md' : 'lg'}
            mt={'md'}
            mb={'md'}
          />

          <CheckboxFieldController
            control={control}
            name={'extra_activities.balloon_modeling'}
            id={'extra_activities.balloon_modeling'}
            label={t('balloon_modeling')}
            size={mobileView ? 'md' : 'lg'}
            mb={'md'}
          />

          {(watchBalloons || watchPainting) && (
            <Grid>
              <Grid.Col span={mobileView ? 12 : 9}>
                <NumberInputController
                  control={control}
                  name={'extra_activities.hours'}
                  id={'extra_activities.hours'}
                  label={t('extra_activities_hours')}
                  placeholder={t('hours')}
                  size={'lg'}
                  mb={'md'}
                  styles={
                    mobileView
                      ? {
                          label: {
                            fontWeight: 'bold',
                            fontSize: '12px !important',
                          },
                        }
                      : {}
                  }
                />
              </Grid.Col>
            </Grid>
          )}
        </>
      )}
      {/*<TextAreaController
        control={control}
        name={'other_info'}
        id={'other_info'}
        label={t('other_requirements_info')}
        size={mobileView ? 'md' : 'lg'}
        placeholder={t('booking.other_requirements_info-field.placeholder')}
        mb={'md'}
        styles={
          mobileView
            ? {
                label: {
                  fontWeight: 'bold',
                  fontSize: '16px !important',
                },
              }
            : {}
        }
      />*/}

      <TextAreaController
        control={control}
        name={'care_expectations'}
        id={'care_expectations'}
        label={t('expectations_for_the_care')}
        size={mobileView ? 'md' : 'lg'}
        placeholder={t('booking.expectations_for_the_care-field.placeholder')}
        mb={'md'}
        styles={
          mobileView
            ? {
                label: {
                  fontWeight: 'bold',
                  fontSize: '16px !important',
                },
              }
            : {}
        }
      />
      {!booking?.is_hotel && (
        <TextInputController
          control={control}
          name={'address'}
          id={'address'}
          size={mobileView ? 'md' : 'lg'}
          label={t('address')}
          placeholder={t('address')}
          mb={'md'}
          styles={
            mobileView
              ? {
                  label: {
                    fontWeight: 'bold',
                    fontSize: '16px !important',
                  },
                }
              : {}
          }
        />
      )}

      {!isLoggedIn && !frame && (
        <TextInputController
          control={control}
          name={'first_name'}
          id={'name'}
          size={mobileView ? 'md' : 'lg'}
          label={t('name')}
          placeholder={t('name')}
          mb={'md'}
          styles={
            mobileView
              ? {
                  label: {
                    fontWeight: 'bold',
                    fontSize: '16px !important',
                  },
                }
              : {}
          }
        />
      )}

      <Grid>
        <Grid.Col md={6}>
          <PhoneInputController
            control={control}
            //trigger={trigger}
            name={'phone'}
            id={'phone'}
            label={t('phone')}
            size={mobileView ? 'md' : 'lg'}
            placeholder={t('phone')}
            required={true}
            mb={'md'}
          />
        </Grid.Col>
      </Grid>

      {((!isLoggedIn && !frame) || (frame && booking.contact_email)) && (
        <TextInputController
          control={control}
          name={'contact_email'}
          id={'email'}
          size={mobileView ? 'md' : 'lg'}
          label={t('email')}
          placeholder={t('email')}
          mt={'md'}
          styles={
            mobileView
              ? {
                  label: {
                    fontWeight: 'bold',
                    fontSize: '16px !important',
                  },
                }
              : {}
          }
        />
      )}
    </>
  )
}
